import React, { useEffect, useMemo, useState } from 'react';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import useAccessFlow from 'core/accessRequestForm/useAccessFlow';
import DateSelector from 'components/Form/DateSelector';
import ShiftSelector from 'components/Form/ShiftSelector';
import CostCode from 'components/Form/CostCode';
import type { SaveShiftValues } from 'components/Form/ShiftSelector/types';
import { Alert, notification } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import { buildShiftOptions, getRetailDayFilter, resolveRetailShiftForDate, validateDatesAgainstOpenDays } from 'core/utils/retailDateHelpers';
import { deleteAccessRequest } from 'core/accessRequestForm/accessRequestFormApi';
import { Shift, VisitRequestFormResponsePayload } from 'core/accessRequestForm/types';
import { isEmpty } from 'underscore';
import moment from 'moment';
import { unwrapResult } from '@reduxjs/toolkit';
import { shiftIsOnCalendarDate, usToIso } from 'core/utils/date';
import { ACCESS_STEPPER_LABELS, ACCESS_STEPPER_LABELS_RETAIL } from 'core/utils/stepperLabels';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import { BankHolidayMap, bankHolidayDates, fetchBankHolidays, toCalendarHolidays } from 'core/utils/bankHolidays';
import { BankHolidayModalState, bankHolidayChargeDates } from 'core/utils/bankHolidayWarning';
import BankHolidayWarningModal from 'components/Modals/BankHolidayWarningModal';
import { HandleDateValues } from './types';

export interface VisitDatesProps {
    id: number | null;
    date: string,
    saved: boolean,
    shiftName?: string | undefined,
    startTime?: string | undefined,
    endTime?: string | undefined,
    range?: boolean | undefined,
    startDate?: string | undefined,
    endDate?: string | undefined,
    shiftType?: number | string | undefined,
    blockDate?: string | undefined | null,
}

interface TimeSlot {
    label: string;
    startTime: string;
    endTime: string;
}

interface ShiftOption {
    id?: number;
    name: string;
    value: string;
    timeSlots: TimeSlot[];
}

interface ShiftSelected {
    _tempIndex?: number | undefined;
    blockDate?: string | undefined | null;
    id?: number | null;
    day?: string;
    date: string;
    shift: string | undefined;
    shiftType: number | string | undefined;
    startTime: string | undefined;
    endTime: string | undefined;
    bookingTimeCritical?: boolean;
    saved?: boolean;
}

interface customShiftProps {
    startTime: string;
    endTime: string;
    date: string;
    blockDate: string | undefined | null;
}

const MOBILE_RESPONSE_MAX_SHIFT_HOURS = 3;

const AccessDateSelectionForm: React.FC = () => {
    const navigation = useNavigate();
    const { state: navState } = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const flow = useAccessFlow();
    const { stepData, visitRequest: visitRequestState, isFetching, isUpdating } = useSelector((state: CombinedStates) => state.accessRequestForm);
    const [selectedDates, setSelectedDates] = useState<Array<string>>([]);
    const [visitDates, setVisitDates] = useState<VisitDatesProps[]>([]);
    const [shiftOptions, setShiftOptions] = useState<ShiftOption[]>([]);
    const [selectedShift, setSelectedShift] = useState<ShiftSelected[]>([]);
    // A bank holiday suspends the site's open window, so a shift on one is
    // charged where it would normally be free — the calendar toggle and the
    // billed-hours figure both read this.
    const [bankHolidays, setBankHolidays] = useState<BankHolidayMap>({});
    const holidays = useMemo(() => toCalendarHolidays(bankHolidays), [bankHolidays]);
    const bankHolidayList = useMemo(() => bankHolidayDates(bankHolidays), [bankHolidays]);
    const [bankHolidayAcknowledged, setBankHolidayAcknowledged] = useState<boolean>(false);
    const [bankHolidayModal, setBankHolidayModal] = useState<BankHolidayModalState>({
        open: false,
        navigationState: null,
    });
    const [code, setCode] = useState<string>('');
    const minimumNoticeDays = stepData?.minimumNoticeDays || 0;
    const minimumCalendarDate = minimumNoticeDays > 0
        ? moment().add(minimumNoticeDays, 'days').format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
    const isMobileResponse = stepData?.isMobileResponse || false;
    const maxShiftHours = isMobileResponse ? MOBILE_RESPONSE_MAX_SHIFT_HOURS : 0;
    const isRetail = stepData?.isRetail || false;
    const openDaysByDay = isRetail ? stepData?.openDaysByDay : undefined;
    const amending = navState?.amending;
    // Auto-cleanup request if user leaves without completing
    useFormCleanup({
        requestId: (!flow.guestMode && !amending) ? visitRequestState.encryptedId : null,
        formBasePath: '/access-request/',
        deleteEndpoint: 'access-request',
        dispatchDelete: () => {
            if (visitRequestState.encryptedId) dispatch(deleteAccessRequest(visitRequestState.encryptedId));
        },
    });

    useEffect(() => {
        fetchBankHolidays().then(setBankHolidays);
    }, []);

    // The saved shifts that a bank holiday turns from free into chargeable.
    const bankHolidayCharges = useMemo(() => bankHolidayChargeDates(
        visitDates.filter((date) => date.saved && date.startTime && date.endTime),
        {
            enabled24_7: stepData?.enabled24_7 ?? false,
            openDays: stepData?.openDays,
            isRetailGeneralVisit: isRetail,
        },
        bankHolidays,
    ), [visitDates, bankHolidays, stepData?.enabled24_7, stepData?.openDays]);

    // Warn again if the affected dates change — adding another bank holiday, or
    // editing one of these shifts, is a fresh charge to be told about. Changing
    // the cost code is not.
    const bankHolidayKey = bankHolidayCharges.map((charge) => charge.date).join('|');
    useEffect(() => {
        setBankHolidayAcknowledged(false);
    }, [bankHolidayKey]);

    const handleNextClick = (navigationState: Record<string, unknown>, skipBankHolidayCheck = false) => {
        let error = { message: '', description: '' };
        if (isEmpty(selectedShift)) {
            error = {
                message: 'Shift Not Selected',
                description: 'Please select a shift to proceed',
            };
        }

        if (visitDates.some((date) => !date.saved)) {
            error = {
                message: 'Shift Not Saved',
                description: 'Please save the shift to proceed',
            };
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }

        // Ahead of the cost-code check below: a bank holiday is why a booking
        // that cost nothing yesterday now needs a cost code, and "Cost Code Not
        // Provided" on its own explains none of that.
        if (!skipBankHolidayCheck && bankHolidayCharges.length > 0 && !bankHolidayAcknowledged) {
            setBankHolidayModal({ open: true, navigationState });
            return;
        }

        // Retail general access is never billed, so the cost code is always N/A
        // and read-only — no user-entered value to validate.
        const submittedCostCode = isRetail ? 'N/A' : code;

        if (submittedCostCode === '') {
            error = {
                message: 'Cost Code Not Provided',
                description: 'Please fill a cost code to proceed',
            };
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }
        if (selectedDates !== null && (id !== undefined || flow.guestMode) && !isFetching && !isUpdating) {
            flow.updateStep(2, {
                selectedDates: selectedShift,
                costCode: { costCentre: submittedCostCode },
                amending,
                forcedStep: 2,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    flow.goToStep(3, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(1, navigationState);
    };

    const handleInitialLoad = async () => {
        if (id !== undefined || flow.guestMode) {
            const resultAction = await flow.loadStep(2, amending);

            // Redirect to dashboard if access request not found
            if (resultAction.meta.requestStatus === 'rejected') {
                // A guest has no dashboard and no way to create a request: send
                // them back to the link itself, which re-validates the token and
                // shows the invalid/expired message when that is the reason.
                if (flow.guestMode) {
                    navigation(`/booking/${flow.token}`, { replace: true });
                    return;
                }

                navigation('/dashboard', { replace: true });
                notification.error({
                    message: 'Access Request Not Found',
                    description: 'The access request could not be found. Please create a new request.',
                });
            }
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Access Request',
                description: 'Please select an access request to proceed',
            });
        }
    };

    const formatDate = (dateString: string) => {
        // Parse explicitly: inputs are MM-DD-YYYY (form state) or YYYY-MM-DD /
        // ISO (API). new Date("MM-DD-YYYY") returns Invalid Date in Safari,
        // which surfaced as "Invalid date" on the calendar.
        const parsed = moment(dateString, ['MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        return parsed.isValid() ? parsed.format('MM-DD-YYYY') : dateString;
    };

    useEffect(() => {
        handleInitialLoad();
    }, []);

    const handleDatesSelect = (values: HandleDateValues) => {
        if (values.dates && !values.dates.includes('Invalid date')) {
            let validDate = values.dates;

            if (!values.includeWeekends) {
                validDate = validDate.filter((date) => {
                    const day = new Date(date).getDay();
                    return day !== 0 && day !== 6;
                });
            }

            if (!values.includeBankHolidays) {
                validDate = validDate.filter((date) => !holidays.includes(date));
            }

            const nextSelectedDates = validDate.map(date => formatDate(date));
            setSelectedDates(nextSelectedDates);

            const formatted = validDate.concat(visitRequestState.dates?.map((date) => {
                const parts = date.date.split('-');
                const formattedDate = `${parts[1]}-${parts[2]}-${parts[0]}`;
                return formattedDate;
            }).filter((formattedDate) => !validDate.includes(formattedDate)) || []);

            validDate = formatted?.filter((date) => validDate.includes(date));
            const filteredOutDate = selectedDates.filter((date) => !validDate.includes(date))
                .concat(formatted?.filter((date) => !validDate.includes(date)) || []);

            const formattedFilteredOut = filteredOutDate.map((date) => {
                const parts = date.split('-');
                return `${parts[2]}-${parts[0]}-${parts[1]}`;
            });

            setSelectedShift((prevSelectedShifts) => prevSelectedShifts.filter((shift) => !formattedFilteredOut.includes(shift.date)));

            const sortedDates = validDate.sort((a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf());
            const selectedVisitDates = sortedDates.map((dates) => {
                const date = formatDate(dates);
                const parts = date.split('-');
                const formattedDate = `${parts[2]}-${parts[0]}-${parts[1]}`;
                const dateId = selectedShift?.find((visitDate) => visitDate.date === dates)?.id || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.id || null;
                const saved = selectedShift?.some((visitDate) => visitDate.date === dates) || selectedShift?.some((visitDate) => visitDate.date === formattedDate) || false;
                const shift = selectedShift?.find((visitDate) => visitDate.date === dates)?.shift || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.shift;
                const startTime = selectedShift?.find((visitDate) => visitDate.date === dates)?.startTime || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.startTime;
                const endTime = selectedShift?.find((visitDate) => visitDate.date === dates)?.endTime || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.endTime;
                const shiftType = selectedShift?.find((visitDate) => visitDate.date === dates)?.shiftType || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.shiftType;
                const blockDate = selectedShift?.find((visitDate) => visitDate.date === dates)?.blockDate || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.blockDate;
                return {
                    id: dateId,
                    date,
                    saved,
                    shiftName: shift,
                    shiftType,
                    startTime,
                    endTime,
                    blockDate,
                };
            });

            setVisitDates(prevDate => {
                const filteredSelectedVisitDates = selectedVisitDates.filter(selectedItem => !prevDate.some(prevItem => prevItem.date === selectedItem.date));
                const filteredPrevDate = prevDate.filter(prevItem => sortedDates.includes(prevItem.date));
                const mergedVisitDates = [...filteredPrevDate, ...filteredSelectedVisitDates];

                const sortedMergeDates = mergedVisitDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                const uniqueVisitDates = sortedMergeDates.reduce((unique: VisitDatesProps[], item) => {
                    const isNullId = item.id === null;
                    const isDuplicate = !isNullId && unique.some(existingItem => existingItem.id === item.id);
                    if (isNullId || !isDuplicate) {
                        unique.push(item);
                    }
                    return unique;
                }, []);

                // Retail-only: shared helper drops newly-selected dates with
                // no opening times configured (kept in sync with guarding).
                if (openDaysByDay) {
                    const newlyAddedDates = new Set(filteredSelectedVisitDates.map((d) => d.date));
                    const { validated, droppedDates } = validateDatesAgainstOpenDays(
                        uniqueVisitDates,
                        newlyAddedDates,
                        openDaysByDay,
                    );

                    if (droppedDates.length > 0) {
                        const validDateStrings = validated.map((vd) => vd.date);
                        setSelectedDates(nextSelectedDates.filter((d) => validDateStrings.includes(d)));
                        notification.error({
                            message: 'Opening Times Not Configured',
                            description: `The following dates have no opening/closing times configured and have been removed: ${droppedDates.join(', ')}. Please contact an administrator.`,
                        });
                    }

                    return validated;
                }

                return uniqueVisitDates;
            });

        }
    };

    function checkForOverlap(newShift: customShiftProps, existingShifts: VisitDatesProps[] | customShiftProps[]): boolean {
        const format = 'MM-DD-YYYY HH:mm:ss';
        const newStart = moment(`${newShift.date} ${newShift.startTime}`, format);
        const newEnd = moment(`${newShift.date} ${newShift.endTime}`, format);

        if (newEnd.isBefore(newStart)) {
            newEnd.add(1, 'day');
        }

        return existingShifts.some(shift => {
            if (shift.startTime === undefined || shift.endTime === undefined) {
                return false;
            }
            const existingStart = moment(`${shift.date} ${shift.startTime}`, format);
            const existingEnd = moment(`${shift.date} ${shift.endTime}`, format);

            if (existingEnd.isBefore(existingStart)) {
                existingEnd.add(1, 'day');
            }

            return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
        });
    }

    const handleSaveShift = (values: SaveShiftValues) => {
        const shifts: customShiftProps[] = [];
        values.dates.sort((a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf());
        // Custom shift logic
        if (values.shift === '24/7' && values.startTime === values.endTime && values.timeSlot !== 'Custom') {
            values.dates.forEach((date) => {
                // ISO form: new Date("MM-DD-YYYY HH:mm") is Invalid Date in
                // Safari, which moment then renders as the literal "Invalid
                // date" into the saved shift.
                const visitStart = `${usToIso(date)}T${values.startTime}`;
                const visitEnd = `${usToIso(date)}T${values.endTime}`;
                const initialEndTime = new Date(visitStart);
                initialEndTime.setHours(initialEndTime.getHours() + 12);
                shifts.push({
                    startTime: moment(new Date(visitStart)).format('HH:mm:ss'),
                    endTime: moment(new Date(initialEndTime)).format('HH:mm:ss'),
                    date: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                    blockDate: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                });
                shifts.push({
                    startTime: moment(new Date(initialEndTime)).format('HH:mm:ss'),
                    endTime: moment(new Date(visitEnd)).format('HH:mm:ss'),
                    date: moment(initialEndTime).format('MM-DD-YYYY'),
                    blockDate: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                });
            });
        }
        if (values.timeSlot === 'Custom') {
            values.dates.forEach((date) => {

                const visitStart = `${usToIso(date)}T${values.customTimeSlotStartTime}`;
                const initialEndTime = new Date(visitStart);
                const startCustom = new Date(visitStart);
                let isExactStart = false;

                if ((startCustom.getHours() === 7 || startCustom.getHours() === 19) && startCustom.getMinutes() === 0) {
                    isExactStart = true;
                    const firstShiftEndTime = new Date(startCustom);
                    firstShiftEndTime.setHours(startCustom.getHours() + 12);
                    shifts.push({
                        startTime: moment(new Date(startCustom)).format('HH:mm:ss'),
                        endTime: moment(firstShiftEndTime).format('HH:mm:ss'),
                        date: moment(startCustom).format('MM-DD-YYYY'),
                        blockDate: moment(startCustom).format('MM-DD-YYYY'),
                    });
                }

                let nextStartTime: Date;
                if (isExactStart) {
                    nextStartTime = new Date(`${usToIso(date)}T${shifts[shifts.length - 1]?.endTime}`) || new Date(startCustom);
                } else {
                    if (startCustom.getHours() < 19) {
                        if (startCustom.getHours() < 19 && startCustom.getHours() >= 7) {
                            initialEndTime.setHours(19, 0, 0, 0); // Set to 7pm same day
                        } else {
                            initialEndTime.setHours(7, 0, 0, 0); // Same day 7am
                        }
                    } else {
                        initialEndTime.setDate(initialEndTime.getDate() + 1);
                        initialEndTime.setHours(7, 0, 0, 0); // Next day 7am
                    }
                    const newShift = {
                        startTime: moment(startCustom).format('HH:mm:ss'),
                        endTime: moment(initialEndTime).format('HH:mm:ss'),
                        date: moment(startCustom).format('MM-DD-YYYY'),
                        blockDate: moment(startCustom).format('MM-DD-YYYY'),
                    };
                    const hasOverlap = checkForOverlap(newShift, shifts);

                    // Check for overlap for the current date's newShift before adding to shifts array
                    if (hasOverlap) {
                        // change start time to end time of last shift
                        const lastShift = shifts[shifts.length - 1] || { endTime: '07:00:00' };
                        initialEndTime.setHours(7, 0, 0, 0);
                        if (lastShift.endTime === '07:00:00') {
                            initialEndTime.setHours(initialEndTime.getHours() + 12);
                        }
                        let initialBlockDate = initialEndTime;
                        const hasDateSelected = visitDates.some((item) => item.date === moment(initialEndTime).format('MM-DD-YYYY'));
                        if (!hasDateSelected) {
                            initialBlockDate = new Date(`${usToIso(values.dates[0] ?? '')}T00:00:00`);
                        }
                        const newShift2 = {
                            startTime: lastShift.endTime,
                            endTime: moment(initialEndTime).format('HH:mm:ss'),
                            date: moment(initialEndTime).format('MM-DD-YYYY'),
                            blockDate: moment(initialBlockDate).format('MM-DD-YYYY'),
                        };
                        shifts.push(newShift2);
                    } else {
                        shifts.push(newShift);
                    }
                    nextStartTime = initialEndTime;
                }

                let nextEndTime = new Date(nextStartTime);
                nextEndTime.setHours(nextStartTime.getHours() + 12);
                if (nextEndTime.getHours() === 19 && nextStartTime.getHours() === 7) {
                    if (startCustom.getHours() > 7 && startCustom.getHours() <= 19) {
                        nextStartTime.setDate(nextStartTime.getDate() + 1);
                    }
                }
                let nextShift: customShiftProps;
                if (startCustom.getHours() > 19) {
                    const hasDateSelected = visitDates.some((item) => item.date === moment(nextStartTime).format('MM-DD-YYYY'));
                    let initialBlockDate = nextStartTime;
                    if (!hasDateSelected) {
                        initialBlockDate = new Date(`${usToIso(values.dates[0] ?? '')}T00:00:00`);
                    }
                    nextShift = {
                        startTime: moment(nextStartTime).format('HH:mm:ss'),
                        endTime: moment(nextEndTime).format('HH:mm:ss'),
                        date: moment(nextStartTime).format('MM-DD-YYYY'),
                        blockDate: moment(initialBlockDate).format('MM-DD-YYYY'),
                    };
                } else {
                    nextShift = {
                        startTime: moment(nextStartTime).format('HH:mm:ss'),
                        endTime: moment(nextEndTime).format('HH:mm:ss'),
                        date: moment(nextStartTime).format('MM-DD-YYYY'),
                        blockDate: moment(startCustom).format('MM-DD-YYYY'),
                    };
                }
                shifts.push(nextShift);

                if (!isExactStart) {
                    nextStartTime = nextEndTime;
                    nextEndTime = new Date(nextStartTime);
                    nextEndTime.setHours(nextStartTime.getHours() + 12);
                    let newShift: customShiftProps;

                    if (startCustom.getHours() > 19) {
                        newShift = {
                            startTime: moment(nextStartTime).format('HH:mm:ss'),
                            endTime: moment(nextEndTime).format('HH:mm:ss'),
                            date: moment(nextStartTime).format('MM-DD-YYYY'),
                            blockDate: moment(nextStartTime).format('MM-DD-YYYY'),
                        };
                    } else {
                        const hasDateSelected = visitDates.some((item) => item.date === moment(initialEndTime).format('MM-DD-YYYY'));
                        let initialBlockDate = startCustom;
                        if (!hasDateSelected) {
                            initialBlockDate = new Date(`${usToIso(values.dates[0] ?? '')}T00:00`);
                        }
                        newShift = {
                            startTime: moment(nextStartTime).format('HH:mm:ss'),
                            endTime: moment(nextEndTime).format('HH:mm:ss'),
                            date: moment(nextStartTime).format('MM-DD-YYYY'),
                            blockDate: moment(initialBlockDate).format('MM-DD-YYYY'),
                        };
                    }
                    if ((shifts[shifts.length - 1]?.endTime !== '07:00:00' && shifts[shifts.length - 2]?.endTime !== '19:00:00') || (startCustom.getHours() > 7 && startCustom.getHours() < 19)) {
                        shifts.push(newShift);
                    }
                }
            });
        }

        if (values.shift === 'Custom') {
            values.dates.forEach((date) => {
                const visitStart = `${usToIso(date)}T${values.customShiftStartTime}`;
                const visitEnd = `${usToIso(date)}T${values.customShiftEndTime}`;
                const initialEndTime = new Date(visitStart);
                const startCustom = new Date(visitStart);
                const endCustom = new Date(visitEnd);

                const startHour = startCustom.getHours();
                const endHour = endCustom.getHours();
                let shiftDuration;
                if (endHour < startHour) {
                    shiftDuration = (24 - startHour) + endHour;
                } else {
                    shiftDuration = endHour - startHour;
                }
                const maxAllowedHours = maxShiftHours || 12;
                if (shiftDuration > maxAllowedHours || shiftDuration === 0) {
                    notification.error({
                        message: 'Invalid Shift Time',
                        description: `The shift you selected is more than ${maxAllowedHours} hours. Please select a valid shift time to create a shift.`,
                    });
                    return;
                }

                // Create a single continuous shift without splitting at 7am/7pm
                const endTime = new Date(visitEnd);
                // If end time is before start time, it crosses midnight
                if (endTime <= startCustom) {
                    initialEndTime.setDate(initialEndTime.getDate() + 1);
                }
                initialEndTime.setHours(endCustom.getHours(), endCustom.getMinutes(), 0, 0);

                const singleShift = {
                    startTime: moment(startCustom).format('HH:mm:ss'),
                    endTime: moment(initialEndTime).format('HH:mm:ss'),
                    date: moment(startCustom).format('MM-DD-YYYY'),
                    blockDate: moment(startCustom).format('MM-DD-YYYY'),
                };
                const hasOverlap = checkForOverlap(singleShift, shifts);
                if (hasOverlap) {
                    notification.error({
                        message: 'Shift Overlap',
                        description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.',
                    });
                    return;
                }
                shifts.push(singleShift);
            });
        }
        if (values.timeSlot !== 'Custom' && values.dates.length > 0 && values.shift !== 'Custom') {
            // Exclude the dates we're (re)saving from the overlap source —
            // otherwise a date being rewritten overlaps with its own existing
            // saved entry, raising a false "Shift Overlap" per date (e.g.
            // single-shift mode re-applies the shift to every date).
            const existingShiftsForOverlap = visitDates.filter((vd) => !values.dates.includes(vd.date));
            let hasOverlap = false;
            values.dates.forEach((date) => {
                const newShift = {
                    startTime: values.startTime || '',
                    endTime: values.endTime || '',
                    date,
                    blockDate: null,
                };

                if (checkForOverlap(newShift, existingShiftsForOverlap)) {
                    notification.error({ message: 'Shift Overlap', description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.' });
                    hasOverlap = true;
                }
            });
            if (hasOverlap) {
                return;
            }
        }

        // Same self-overlap guard: a date being rewritten can't overlap with
        // its own previous entry.
        const rewrittenDates = new Set([...values.dates, ...shifts.map((s) => s.date)]);
        const existingShiftsForOverlap = visitDates.filter((vd) => !rewrittenDates.has(vd.date));
        const hasOverlap = shifts.some((shift) => checkForOverlap(shift, existingShiftsForOverlap));
        if (hasOverlap) {
            notification.error({ message: 'Shift Overlap', description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.' });
            return;
        }

        const completeVisitDates = [...visitDates];
        const uniqueblockDateShifts = shifts.filter((shift, index, self) => index === self.findIndex((t) => t.blockDate === shift.blockDate));

        uniqueblockDateShifts.forEach(shiftToUpdate => {
            const matchingIndex = completeVisitDates.findIndex(visitDate => visitDate.date === shiftToUpdate.blockDate && !visitDate.blockDate);

            if (matchingIndex > -1) {
                completeVisitDates[matchingIndex] = {
                    ...completeVisitDates[matchingIndex], // Preserve existing properties
                    date: shiftToUpdate.date,
                    startTime: shiftToUpdate.startTime,
                    endTime: shiftToUpdate.endTime,
                    blockDate: shiftToUpdate.blockDate,
                    saved: completeVisitDates[matchingIndex]?.saved || false,
                    id: completeVisitDates[matchingIndex]?.id || null,
                };
            }
        });

        shifts.forEach((shift) => {
            if (!completeVisitDates.some(date => date.date === shift.date && date.startTime === shift.startTime)) {
                completeVisitDates.push({
                    startTime: shift.startTime,
                    endTime: shift.endTime,
                    startDate: shift.date,
                    endDate: shift.date,
                    date: shift.date,
                    saved: false,
                    id: null,
                    blockDate: shift.blockDate,
                });
            }
        });

        const retailSkippedDates: string[] = [];
        // Retail general: dates whose picked start falls outside the site's
        // open-day window (before opening or at/after closing).
        const outsideHoursDates: string[] = [];
        // Compare times by minutes-since-midnight so "HH:mm" and "HH:mm:ss"
        // strings compare correctly regardless of format.
        const toMinutes = (time?: string): number | null => {
            if (!time) return null;
            const [h, m] = time.split(':').map(Number);
            return (h || 0) * 60 + (m || 0);
        };
        const updatedVisitDates = completeVisitDates.map((date) => {
            const customShift = shifts.find(shift => shift.date === date.date && shift.startTime === date.startTime);
            if (customShift) {
                return {
                    ...date,
                    saved: true,
                    shiftName: values.shift,
                    startTime: customShift.startTime,
                    endTime: customShift.endTime,
                    blockDate: customShift.blockDate,
                    shiftType: values.shiftType ? parseInt(values.shiftType, 10) : 0,
                };
            } if (values.dates.includes(date.date) && (date.startTime || values.startTime)) {
                let shiftStart: string | undefined = values.startTime || date.startTime;
                let shiftEnd: string | undefined = values.endTime || date.endTime;
                let shiftName = values.shift;
                let shiftType = values.shiftType ? parseInt(values.shiftType, 10) : 0;

                // Retail general/access: the user picks only the START time; the
                // END time defaults to the site's open-day closing time for that
                // date (hidden in the UI, still persisted). The shift name/type
                // are taken from that date's shift row where available.
                if (isRetail) {
                    const weekday = moment(date.date, 'MM-DD-YYYY').format('dddd').toLowerCase();
                    const dayNum = moment(date.date, 'MM-DD-YYYY').day();
                    const openDay = openDaysByDay?.[weekday];
                    const resolved = stepData?.shifts
                        ? resolveRetailShiftForDate(stepData.shifts, date.date, values.shift ?? '')
                        : null;
                    // The retail UI hides the shift dropdown, so if the name-based
                    // resolve misses, fall back to this weekday's shift row directly
                    // so we never persist an empty shift name/type.
                    const dayShiftRow = stepData?.shifts?.find((s) => Number(s.day) === dayNum);

                    // Opening window for this date: prefer the open-day row, fall
                    // back to the resolved shift row.
                    const openStart = openDay?.startTime ?? resolved?.startTime;
                    const openEnd = openDay?.endTime ?? resolved?.endTime;

                    if (!openEnd) {
                        retailSkippedDates.push(
                            moment(date.date, 'MM-DD-YYYY').format('DD/MM/YYYY'),
                        );
                        return date;
                    }

                    const pickedStart = values.startTime || date.startTime || openStart;
                    const startMin = toMinutes(pickedStart);
                    const openStartMin = toMinutes(openStart);
                    const openEndMin = toMinutes(openEnd);

                    // Enforce opening hours: the start must be within the site's
                    // open-day window (>= opening, < closing).
                    if (
                        startMin === null
                        || openEndMin === null
                        || (openStartMin !== null && startMin < openStartMin)
                        || startMin >= openEndMin
                    ) {
                        // Report the specific day with its own opening window so the
                        // user can see which day failed and the valid range for it.
                        const window = openStart ? `${openStart.substring(0, 5)}-${openEnd.substring(0, 5)}` : '';
                        outsideHoursDates.push(
                            `${moment(date.date, 'MM-DD-YYYY').format('DD/MM/YYYY')}${window ? ` (opens ${window})` : ''}`,
                        );
                        return date;
                    }

                    // Keep the user-picked start; end defaults to the open-day
                    // closing time (hidden in the UI, still persisted).
                    shiftStart = pickedStart;
                    shiftEnd = openEnd;
                    shiftName = resolved?.shiftName ?? dayShiftRow?.type ?? shiftName;
                    shiftType = resolved?.shiftType
                        ?? (dayShiftRow ? parseInt(String(dayShiftRow.typeId), 10) : shiftType);
                }

                return {
                    ...date,
                    saved: true,
                    shiftName,
                    startTime: shiftStart,
                    endTime: shiftEnd,
                    shiftType,
                };
            }
            return date;
        });

        if (retailSkippedDates.length > 0) {
            notification.error({
                message: 'Shift Not Available',
                description: `${values.shift} is not available for: ${retailSkippedDates.join(', ')}.`,
            });
        }

        if (outsideHoursDates.length > 0) {
            notification.error({
                message: 'Outside Opening Hours',
                description: `The start time is outside the site's opening hours for: ${outsideHoursDates.join(', ')}.`,
            });
        }

        // sort by date
        updatedVisitDates.sort((a, b) => {
            const dateA = a.date || '';
            const dateB = b.date || '';

            // Compare dates
            if (dateA < dateB) {
                return -1;
            } if (dateA > dateB) {
                return 1;
            }
            return 0;
        });

        const newShifts: ShiftSelected[] = [];
        updatedVisitDates.forEach((day, index) => {
            const dateString = day.date || '';
            const parts = dateString.split('-');
            const formattedDate = `${parts[2]}-${parts[0]}-${parts[1]}`;

            if (day.saved && day.shiftName && day.startTime && day.endTime) {
                newShifts.push({
                    ...day,
                    id: day.id || null,
                    _tempIndex: index,
                    date: formattedDate,
                    day: dateString,
                    shift: day.shiftName,
                    shiftType: day.shiftType,
                    startTime: day.startTime,
                    endTime: day.endTime,
                    blockDate: day.blockDate,
                });
            }
        });

        // newShifts is already a complete mirror of updatedVisitDates' saved
        // entries, so set it outright. Appending to the previous list and
        // deduping on date+startTime kept stale rows alive: a resaved date whose
        // start time changed produced a different key, so the old shift survived
        // alongside the new one (Night 17:30 + Custom 15:00 on the same day) and
        // that list is what gets sent on Next.
        setSelectedShift(newShifts);

        setVisitDates(updatedVisitDates);
    };

    function transformShiftData(shifts: Shift[], filterDayNums?: number[]) {
        return buildShiftOptions(shifts, filterDayNums) as ShiftOption[];
    }

    const handleRemoveShift = (date: string) => {
        const dateToRemove = visitDates.find(visitDate => visitDate.date === date && visitDate.blockDate);
        const blockDateToRemove = dateToRemove?.blockDate;
        const updatedVisitDates = visitDates.map((visitDate) => {
            if (visitDate.date === date && !visitDate.blockDate) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                };
            } if (visitDate.blockDate === blockDateToRemove && blockDateToRemove !== undefined) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                    blockDate: visitDate.blockDate,
                };
            }
            return visitDate;
        });

        const blockDateExists = updatedVisitDates.some((visitDate) => visitDate.blockDate === blockDateToRemove);
        if (blockDateExists && blockDateToRemove !== undefined) {
            const uniqueblockDates = new Set(); // Set to track unique blockDates
            const filteredVisitDates = updatedVisitDates.filter(day => {
                if (!day.blockDate || !uniqueblockDates.has(day.blockDate) || day.blockDate !== blockDateToRemove) {
                    uniqueblockDates.add(day.blockDate);
                    return true;
                }
                return false;
            });
            // update blockDate to be undefinded
            const updatedblockDateDates = filteredVisitDates.map((visitDate) => {
                if (visitDate.blockDate === blockDateToRemove) {
                    return {
                        ...visitDate,
                        date: visitDate.blockDate || '',
                        blockDate: null,
                    };
                }
                return visitDate;
            });
            updatedblockDateDates.sort((a, b) => {
                const dateA = a.date || '';
                const dateB = b.date || '';

                // Compare dates
                if (dateA < dateB) {
                    return -1;
                } if (dateA > dateB) {
                    return 1;
                }
                return 0;
            });
            setVisitDates(updatedblockDateDates);
            setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(shift => shift.blockDate !== blockDateToRemove));
        } else {
            setVisitDates(updatedVisitDates);
        }
        // Matching only on `day` never matched a row hydrated from the API on
        // amend (those have no `day`), so removing or editing an already-saved
        // shift left it in the payload — swapping a Night shift for a Custom one
        // sent both and the request kept two shifts on the same date.
        setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(
            shift => !shiftIsOnCalendarDate(shift, date),
        ));
    };

    const handleEditShift = (date: string) => {
        const dateToRemove = visitDates.find(visitDate => visitDate.date === date && visitDate.blockDate);
        const blockDateToRemove = dateToRemove?.blockDate;
        const updatedVisitDates = visitDates.map((visitDate) => {
            if (visitDate.date === date && !visitDate.blockDate) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    shiftType: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                };
            } if (visitDate.blockDate === blockDateToRemove && blockDateToRemove !== undefined) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    shiftType: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                    blockDate: visitDate.blockDate,
                    date: visitDate.blockDate || '',
                };
            }
            return visitDate;
        });

        const blockDateExists = updatedVisitDates.some((visitDate) => visitDate.blockDate === blockDateToRemove);
        if (blockDateExists && blockDateToRemove !== undefined) {
            const uniqueblockDates = new Set(); // Set to track unique blockDates
            const filteredVisitDates = updatedVisitDates.filter(day => {
                if (!day.blockDate || !uniqueblockDates.has(day.blockDate) || day.blockDate !== blockDateToRemove) {
                    uniqueblockDates.add(day.blockDate);
                    return true;
                }
                return false;
            });
            // update blockDate to be undefinded
            const updatedblockDateDates = filteredVisitDates.map((visitDate) => {
                if (visitDate.blockDate === blockDateToRemove) {
                    return {
                        ...visitDate,
                        blockDate: null,
                    };
                }
                return visitDate;
            });
            updatedblockDateDates.sort((a, b) => {
                const dateA = a.date || '';
                const dateB = b.date || '';

                // Compare dates
                if (dateA < dateB) {
                    return -1;
                } if (dateA > dateB) {
                    return 1;
                }
                return 0;
            });
            setVisitDates(updatedblockDateDates);
            setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(shift => shift.blockDate !== blockDateToRemove));
        } else {
            setVisitDates(updatedVisitDates);
        }
        // Matching only on `day` never matched a row hydrated from the API on
        // amend (those have no `day`), so removing or editing an already-saved
        // shift left it in the payload — swapping a Night shift for a Custom one
        // sent both and the request kept two shifts on the same date.
        setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(
            shift => !shiftIsOnCalendarDate(shift, date),
        ));
    };

    useEffect(() => {
        setSelectedShift(visitRequestState.dates || []);
        const dates = visitRequestState.dates?.reduce((acc: string[], curr) => {
            if (curr.blockDate === null) {
                acc.push(curr.date);
            } else {
                const alreadyIncluded = acc.some(date => {
                    const dateEntry = visitRequestState?.dates?.find(d => d.date === date);
                    return dateEntry?.blockDate === curr.blockDate && dateEntry?.date === curr.date;
                });
                if (!alreadyIncluded) {
                    acc.push(curr.date);
                }
            }
            return acc;
        }, []) || [];

        // set initial dates to setVisitDates
        const allDate = visitRequestState.dates?.reduce((acc: {
            shift: string;
            id?: number;
            date:string;
            startTime: string;
            endTime: string;
            shiftType: string;
            blockDate?: string;}[], curr) => {
            if (curr.id === null || !acc.some(item => item.id === curr.id)) {
                acc.push(curr);
            }
            return acc;
        }, []) || [];
        const currentVisitDates = allDate.map((day) => {
            const date = formatDate(day.date);
            const parts = date.split('-');
            const formattedDate = `${parts[2]}-${parts[0]}-${parts[1]}`;
            const saved = visitRequestState.dates?.some((visitDate) => visitDate.date === day.date) || visitRequestState.dates?.some((visitDate) => visitDate.date === formattedDate) || false;

            return {
                id: day.id || null,
                date,
                saved,
                shiftName: day.shift,
                shiftType: day.shiftType,
                startTime: day.startTime,
                endTime: day.endTime,
                blockDate: day.blockDate,
            };
        });
        currentVisitDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setSelectedDates(dates.map(date => formatDate(date)));
        setVisitDates(currentVisitDates);
    }, [visitRequestState.dates]);

    useEffect(() => {
        if (stepData?.costCode) {
            setCode(stepData.costCode.costCentre || '');
        }
    }, [stepData?.costCode]);

    useEffect(() => {
        if (stepData?.shifts) {
            const dayFilter = getRetailDayFilter(visitDates, isRetail);
            const options = transformShiftData(stepData.shifts, dayFilter);
            // Mobile-response worktypes cap shifts at 3h, and the backend
            // only enforces that cap on Custom shifts — so hide every
            // predefined shift to force the user down the Custom path.
            setShiftOptions(isMobileResponse ? options.filter((o) => o.name === 'Custom') : options);
        }
    }, [stepData.shifts, selectedDates, selectedShift, isRetail, visitDates, isMobileResponse]);

    const siteConfigError = stepData?.siteConfigError;

    return (
        <RequestFormLayout
            currentStep={isRetail ? 1 : 2}
            isNextEnabled={!siteConfigError}
            isBackEnabled={!isRetail}
            {...(isRetail ? {} : { onBackClick: handleBackClick })}
            onNextClick={handleNextClick}
            stepperLabels={isRetail ? ACCESS_STEPPER_LABELS_RETAIL : ACCESS_STEPPER_LABELS}
            formTitle="Access Request Form"
            reduxSliceKey="accessRequestForm">
            {siteConfigError ? (
                <Alert
                    type="error"
                    showIcon
                    message="Site Configuration Required"
                    description={siteConfigError}
                    style={{ marginBottom: 24 }} />
            ) : (
                <>
                    <DateSelector
                        workTypeName={stepData?.workTypeName || visitRequestState.workTypeName}
                        minimumNoticeDays={minimumNoticeDays}
                        handleDatesSelect={handleDatesSelect}
                        selectedDates={selectedDates}
                        selectedShift={selectedShift}
                        holidays={holidays}
                        minDate={minimumCalendarDate} />
                    <ShiftSelector
                        visitDates={visitDates}
                        shiftOptions={shiftOptions}
                        handleRemoveShift={handleRemoveShift}
                        handleSaveShift={handleSaveShift}
                        handleEditShift={handleEditShift}
                        enabled24_7={stepData.enabled24_7}
                        showBilling={false}
                        maxShiftHours={maxShiftHours}
                        isRetail={isRetail}
                        openDaysByDay={openDaysByDay}
                        bankHolidays={bankHolidayList}
                        approvedVisits={stepData?.approvedVisits ?? []} />
                    <CostCode
                        code={code}
                        requireEmail={false}
                        showInfo={false}
                        // Retail general access is never billed, so the cost code is
                        // always N/A and read-only (billedHours <= 0 triggers the
                        // component's N/A state, same as the no-billing case).
                        {...(isRetail ? { billedHours: 0 } : {})}
                        onChangeCode={(value) => setCode(value)} />
                    <BankHolidayWarningModal
                        open={bankHolidayModal.open}
                        dates={bankHolidayCharges}
                        onBack={() => setBankHolidayModal({ open: false, navigationState: null })}
                        onContinue={() => {
                            const { navigationState } = bankHolidayModal;
                            setBankHolidayAcknowledged(true);
                            setBankHolidayModal({ open: false, navigationState: null });
                            // Straight on with the rest of handleNextClick: re-entering
                            // it would read the acknowledgement before React has set it
                            // and reopen the warning.
                            handleNextClick(navigationState ?? {}, true);
                        }} />
                </>
            )}
        </RequestFormLayout>
    );
};

export default AccessDateSelectionForm;
